import { Metadata } from "next";
import { PRIMARY_DOMAIN } from "apps/website/constants/website";
import { PRODUCTION_URLS } from "apps/website/constants";
import { trimTrailingSlashes } from "apps/website/utils/misc/strings";

const TRACKING_PARAM_NAMES = new Set([ "discount_code", "situation", "postLogin", "_gl" ]);

export const isTrackingParam = (key: string): boolean => TRACKING_PARAM_NAMES.has(key) || key.startsWith("utm_");

export type CanonicalQuery = Record<string, string | string[] | undefined>;

export const buildCanonicalUrl = (path: string, query?: CanonicalQuery): string => {
  const [ pathname ] = path.split(/[?#]/);
  const cleanedPath = pathname
    .replace(/^\/+/, "")
    .split("/")
    .map(encodeURIComponent)
    .join("/");

  const params = new URLSearchParams();
  Object.entries(query ?? {}).forEach(([ key, value ]) => {
    if (value === undefined || isTrackingParam(key)) {
      return;
    }
    (Array.isArray(value) ? value : [ value ]).forEach((entry) => params.append(key, entry));
  });
  const search = params.toString();
  const querySuffix = search ? `?${search}` : "";

  return `${PRIMARY_DOMAIN}/${cleanedPath}${querySuffix}`;
};

const ORIGIN_PATTERN = /^(?:https?:)?\/\/([^/?#]+)/i;

const PRODUCTION_HOSTS = new Set(PRODUCTION_URLS.map((url) => new URL(url).host.toLowerCase()));

const splitOrigin = (rawValue: string): { host: string | null; rest: string } => {
  const value = rawValue.trim();

  if (value.startsWith("/") && !value.startsWith("//")) {
    return { host: null, rest: value };
  }

  const withOriginMarker = ORIGIN_PATTERN.test(value) ? value : `//${value}`;
  const match = ORIGIN_PATTERN.exec(withOriginMarker);

  return match
    ? { host: match[1], rest: withOriginMarker.slice(match[0].length) }
    : { host: null, rest: value };
};

export const isSameOriginUrl = (value: string): boolean => {
  const { host } = splitOrigin(value);

  return host === null || PRODUCTION_HOSTS.has(host.toLowerCase());
};

export const toCanonicalPath = (value: string): string => {
  const { rest } = splitOrigin(value);
  const [ withoutQuery ] = rest.split(/[?#]/);
  const withLeadingSlash = withoutQuery.startsWith("/") ? withoutQuery : `/${withoutQuery}`;

  return trimTrailingSlashes(withLeadingSlash, 1);
};

export const resolveCanonicalUrl = (path: string, canonicalUrl?: string): string => {
  if (!canonicalUrl) {
    return buildCanonicalUrl(path);
  }

  if (!isSameOriginUrl(canonicalUrl)) {
    return canonicalUrl.trim();
  }

  return buildCanonicalUrl(toCanonicalPath(canonicalUrl));
};

export const buildSlugPath = (...segments: (string | undefined)[]): string => {
  const cleaned = segments.filter((segment): segment is string => Boolean(segment));
  return cleaned.length ? `/${cleaned.join("/")}` : "/";
};

export const getJoinedSlug = (slug: string[] | undefined): string => {
  if (Array.isArray(slug)) {
    return slug.join("/");
  }
  return slug ?? "";
};

export const buildCanonicalMetadata = (
  path: string,
  overrides?: Metadata,
  query?: CanonicalQuery,
): Metadata => ({
  ...overrides,
  alternates: {
    ...overrides?.alternates,
    canonical: buildCanonicalUrl(path, query),
  },
});
